<template>
  <div class="ui-treepicker">
    <!-- Collapsed, select-like control. The panel expands IN FLOW (not a
         floating popover) so it can never be clipped by the chat scroll
         container — and behaves identically inside an MCP iframe.
         The clear button is an absolutely-positioned SIBLING (a button
         can't legally nest inside a button). -->
    <div class="ui-treepicker__controlwrap">
      <button
        type="button"
        class="ui-treepicker__control"
        :class="{ 'ui-treepicker__control--clearable': selectedLabel }"
        :aria-expanded="open ? 'true' : 'false'"
        @click="toggleOpen"
      >
        <span :class="{ 'ui-treepicker__placeholder': !selectedLabel }">
          {{ selectedLabel || placeholder }}
        </span>
        <span class="ui-treepicker__chevron">{{ open ? '▴' : '▾' }}</span>
      </button>
      <button
        v-if="selectedLabel"
        type="button"
        class="ui-treepicker__clear"
        title="Clear selection"
        @click.stop="clearPick"
      >✕</button>
    </div>
    <div v-if="open" class="ui-treepicker__panel">
      <!-- Type-to-filter: matches the SELECTABLE (bottom-level) nodes by
           name and lists them flat with their path for context; an empty
           box shows the normal browsable tree. -->
      <input
        v-model="query"
        v-focus
        type="text"
        class="ui-treepicker__search"
        :placeholder="freeText ? 'Type a new value, or filter the list…' : 'Type to filter…'"
        @keydown.esc="query = ''"
        @keydown.enter.prevent="freeText && query.trim() ? pickFree() : null"
      />
      <template v-if="query.trim()">
        <!-- Free entry: the typed text is usable as-is, list pick optional. -->
        <button
          v-if="freeText"
          type="button"
          class="ui-treepicker__result ui-treepicker__result--free"
          @click="pickFree"
        >
          <span class="ui-treepicker__result-label">Use “{{ query.trim() }}”</span>
          <span class="ui-treepicker__result-path">New — not in the list</span>
        </button>
        <button
          v-for="row in searchResults()"
          :key="row.key"
          type="button"
          class="ui-treepicker__result"
          :class="{ 'ui-treepicker__result--selected': selectedKey === row.key }"
          :title="row.hint || ''"
          @click="pick(row)"
        >
          <span class="ui-treepicker__result-label">{{ row.label }}</span>
          <span v-if="row.path.length > 1" class="ui-treepicker__result-path">
            {{ row.path.slice(0, -1).join(' / ') }}
          </span>
        </button>
        <div v-if="searchResults().length === 0 && !freeText" class="ui-treepicker__empty">
          No matches — try a different name.
        </div>
        <div v-else-if="searchCapped" class="ui-treepicker__empty">
          Showing the first {{ maxResults }} matches — keep typing to narrow.
        </div>
      </template>
      <template v-else>
        <div
          v-for="row in rows()"
          :key="row.key"
          class="ui-treepicker__row"
          :style="{ paddingLeft: `${row.depth * 18}px` }"
        >
          <button
            v-if="row.hasKids"
            type="button"
            class="ui-treepicker__toggle"
            :aria-expanded="row.expanded ? 'true' : 'false'"
            @click="toggle(row)"
          >{{ row.expanded ? '▾' : '▸' }}</button>
          <span v-else class="ui-treepicker__toggle ui-treepicker__toggle--none"></span>
          <button
            v-if="row.selectable"
            type="button"
            class="ui-treepicker__leaf"
            :class="{ 'ui-treepicker__leaf--selected': selectedKey === row.key }"
            :title="row.hint || ''"
            @click="pick(row)"
          >{{ row.label }}</button>
          <span v-else class="ui-treepicker__branch" @click="toggle(row)">{{ row.label }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, reactive, ref, set } from 'vue';

/** Search results shown per query — trees are server-capped at 500
 * nodes, so collecting every match is cheap; the display cap keeps the
 * panel scannable. */
const MAX_SEARCH_RESULTS = 50;

/**
 * Shared hierarchy treepicker for ui_block cards (form `tree` fields,
 * job_search tree filters). Renders the engine-resolved node tree as an
 * indented, expandable list; only `selectable` nodes (the target level)
 * can be picked. Emits `pick` with `{ value, node_id?, path }` — the
 * same payload shape form fields record.
 *
 * NB expanded-branch keys are added dynamically, so writes go through
 * set() (Vue 2.7 reactive() can't see new keys).
 */
export default defineComponent({
  name: 'UiBlockTreePicker',
  directives: {
    // Focus the filter box as soon as the panel opens.
    focus: { inserted: (el) => el.focus() },
  },
  props: {
    tree: { type: Array, required: true },
    placeholder: { type: String, default: '— select —' },
    /** Pre-selected pick (`{ value, node_id?, path }`) — displayed as the
     * current selection without emitting; the host seeds its own value.
     * Used by the doc_upload hierarchy confirmation to show the
     * chain-identified placement for the user to keep or change. */
    initial: { type: Object, default: null },
    /** The filter box doubles as free entry: typing offers a
     * "Use “…”" pick that emits `{ value }` with NO node_id/path —
     * "enter a value or pick from the list". */
    freeText: { type: Boolean, default: false },
  },
  emits: ['pick', 'clear'],
  setup(props, { emit }) {
    const open = ref(false);
    // A free-text initial ({ value } with no path — e.g. a discarded
    // gate re-showing the form) displays as the bare value.
    const selectedLabel = ref(
      props.initial && Array.isArray(props.initial.path) && props.initial.path.length
        ? props.initial.path.join(' / ')
        : props.initial && typeof props.initial.value === 'string'
          ? props.initial.value
          : '',
    );
    // The expanded map must sit one level INSIDE the reactive object:
    // reading `state.expanded` in rows() goes through a tracked getter,
    // which subscribes the render to the inner object — so set() on it
    // re-renders. A root-level reactive({}) reached via closure never
    // gets a subscriber in Vue 2.7, and set() would notify nobody
    // (clicks would update state but the tree would never redraw).
    //
    // A pre-selected pick's ANCESTOR branches start expanded so opening
    // the panel shows the selection in place, not a collapsed root
    // list. Keys are seeded BEFORE reactive() so Vue 2.7 converts them
    // (post-hoc additions would need set()).
    const initialExpanded = {};
    if (props.initial && Array.isArray(props.initial.path)) {
      let key = '';
      for (let i = 0; i < props.initial.path.length - 1; i++) {
        key = `${key}/${props.initial.path[i]}`;
        initialExpanded[key] = true;
      }
    }
    const state = reactive({ expanded: initialExpanded });
    const selectedKey = ref(
      props.initial && Array.isArray(props.initial.path) && props.initial.path.length
        ? `/${props.initial.path.join('/')}`
        : '',
    );

    function rows() {
      const out = [];
      const walk = (nodes, depth, parentKey, parentPath) => {
        for (const node of nodes) {
          const key = `${parentKey}/${node.label}`;
          const path = [...parentPath, node.label];
          const hasKids = Array.isArray(node.children) && node.children.length > 0;
          const isExpanded = !!state.expanded[key];
          out.push({
            key,
            label: node.label,
            hint: node.hint,
            depth,
            hasKids,
            expanded: isExpanded,
            selectable: node.selectable === true,
            node,
            path,
          });
          if (hasKids && isExpanded) walk(node.children, depth + 1, key, path);
        }
      };
      walk(props.tree || [], 0, '', []);
      return out;
    }

    function toggle(row) {
      if (!row.hasKids) return;
      set(state.expanded, row.key, !state.expanded[row.key]);
    }

    // Type-to-filter over the SELECTABLE nodes (the pickable bottom
    // level): case-insensitive contains on the node's own name; results
    // carry the same row shape pick() takes from the tree walk.
    const query = ref('');
    const searchState = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return { items: [], capped: false };
      const items = [];
      const walk = (nodes, parentKey, parentPath) => {
        for (const node of nodes) {
          const key = `${parentKey}/${node.label}`;
          const path = [...parentPath, node.label];
          if (node.selectable === true && String(node.label).toLowerCase().includes(q)) {
            items.push({ key, label: node.label, hint: node.hint, path, node });
          }
          if (Array.isArray(node.children) && node.children.length) walk(node.children, key, path);
        }
      };
      walk(props.tree || [], '', []);
      return {
        items: items.slice(0, MAX_SEARCH_RESULTS),
        capped: items.length > MAX_SEARCH_RESULTS,
      };
    });

    function toggleOpen() {
      open.value = !open.value;
      // A fresh open starts unfiltered — stale queries hide the tree.
      if (open.value) query.value = '';
    }

    function pick(row) {
      selectedKey.value = row.key;
      selectedLabel.value = row.path.join(' / ');
      open.value = false;
      query.value = '';
      emit('pick', {
        value: row.node.value,
        ...(row.node.node_id !== undefined ? { node_id: row.node.node_id } : {}),
        path: row.path,
      });
    }

    /** Free entry (freeText only): use the typed text as the value —
     * no node_id/path, so node-keyed consumers know it's not a pick. */
    function pickFree() {
      const text = query.value.trim();
      if (!text) return;
      selectedKey.value = `free:${text}`;
      selectedLabel.value = text;
      open.value = false;
      query.value = '';
      emit('pick', { value: text });
    }

    /** The ✕ on the control: reset the picker AND tell the host to
     * drop its recorded value (the picker owns the selection display,
     * so hosts render no separate "Selected:" line). */
    function clearPick() {
      selectedKey.value = '';
      selectedLabel.value = '';
      query.value = '';
      emit('clear');
    }

    return {
      rows,
      toggle,
      pick,
      pickFree,
      clearPick,
      selectedKey,
      open,
      toggleOpen,
      selectedLabel,
      query,
      searchResults: () => searchState.value.items,
      searchCapped: computed(() => searchState.value.capped),
      maxResults: MAX_SEARCH_RESULTS,
    };
  },
});
</script>

<style scoped>
.ui-treepicker__controlwrap {
  position: relative;
}
.ui-treepicker__clear {
  position: absolute;
  top: 50%;
  right: 26px;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: none;
  font-size: 11px;
  line-height: 1;
  color: rgb(107 114 128);
  cursor: pointer;
}
.ui-treepicker__clear:hover {
  background: rgb(238 242 255);
  color: rgb(67 56 202);
}
.ui-treepicker__control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgb(229 231 235);
  background: white;
  font: inherit;
  font-size: 14px;
  color: rgb(17 24 39);
  cursor: pointer;
  transition: border-color 0.15s ease;
  text-align: left;
}
.ui-treepicker__control:focus-visible {
  outline: none;
  border-color: rgb(129 140 248);
  box-shadow: none;
}
/* Room for the absolutely-positioned ✕ (after the base rule — its
   padding shorthand would win the cascade otherwise). */
.ui-treepicker__control--clearable {
  padding-right: 48px;
}
.ui-treepicker__placeholder {
  color: rgb(156 163 175);
}
.ui-treepicker__chevron {
  flex: 0 0 auto;
  font-size: 10px;
  color: rgb(107 114 128);
}
.ui-treepicker__panel {
  margin-top: 4px;
  max-height: 260px;
  overflow-y: auto;
  padding: 4px 6px;
  border: 1px solid rgb(209 213 219);
  border-radius: 8px;
  background: white;
}
.ui-treepicker__search {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 4px;
  padding: 5px 8px;
  border: 1px solid rgb(229 231 235);
  border-radius: 6px;
  font: inherit;
  font-size: 12.5px;
  color: rgb(31 41 55);
}
.ui-treepicker__search:focus {
  outline: none;
  border-color: rgb(99 102 241);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}
.ui-treepicker__search::placeholder {
  color: rgb(156 163 175);
}
.ui-treepicker__result {
  display: block;
  width: 100%;
  border: 0;
  background: none;
  padding: 4px 8px;
  border-radius: 6px;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.ui-treepicker__result:hover {
  background: rgb(238 242 255);
}
/* Free-entry row: visually set off from list matches. */
.ui-treepicker__result--free {
  border-bottom: 1px dashed rgb(229 231 235);
  border-radius: 6px 6px 0 0;
  margin-bottom: 2px;
}
.ui-treepicker__result--free .ui-treepicker__result-label {
  color: rgb(67 56 202);
  font-weight: 500;
}
.ui-treepicker__result--selected {
  background: rgb(79 70 229);
}
.ui-treepicker__result--selected .ui-treepicker__result-label,
.ui-treepicker__result--selected .ui-treepicker__result-path {
  color: white;
}
.ui-treepicker__result-label {
  display: block;
  font-size: 13px;
  color: rgb(31 41 55);
}
.ui-treepicker__result-path {
  display: block;
  font-size: 11px;
  color: rgb(107 114 128);
}
.ui-treepicker__empty {
  padding: 4px 8px;
  font-size: 11px;
  font-style: italic;
  color: rgb(107 114 128);
}
.ui-treepicker__row {
  display: flex;
  align-items: center;
  gap: 2px;
  min-height: 24px;
}
.ui-treepicker__toggle {
  flex: 0 0 18px;
  width: 18px;
  border: 0;
  background: none;
  padding: 0;
  font-size: 11px;
  color: rgb(107 114 128);
  cursor: pointer;
  line-height: 1;
}
.ui-treepicker__toggle--none {
  cursor: default;
}
.ui-treepicker__branch {
  flex: 1;
  padding: 2px 4px;
  border-radius: 6px;
  font-size: 13px;
  color: rgb(75 85 99);
  cursor: pointer;
  user-select: none;
}
.ui-treepicker__branch:hover {
  background: rgb(243 244 246);
}
.ui-treepicker__leaf {
  border: 0;
  background: none;
  padding: 2px 8px;
  border-radius: 6px;
  font: inherit;
  font-size: 13px;
  color: rgb(31 41 55);
  cursor: pointer;
  text-align: left;
}
.ui-treepicker__leaf:hover {
  background: rgb(238 242 255);
}
.ui-treepicker__leaf--selected {
  background: rgb(79 70 229);
  color: white;
}
.ui-treepicker__leaf--selected:hover {
  background: rgb(67 56 202);
}
</style>
